import { IGroup } from '@fluentui/react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import React from 'react';
import {
  FluentProvider,
  webLightTheme,
  Table,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Button,
  TableCell,
  TableBody,
  makeStyles,
} from '@fluentui/react-components';
import { Link } from 'react-router-dom';
import { ChevronRightRegular, ChevronDownRegular } from '@fluentui/react-icons';
import { RoleActions } from '../../utils/statApi/RolesApi';
import Card from '../common/card/Card';
import SpanWithLabel from '../common/SpanWithLabel';
import StackColumn from '../common/StackColumn';
import { useCollapsedGroups } from './useCollapsedGroups';

interface IRoleDetail {
  roleActions: RoleActions;
}

const useStyles = makeStyles({
  groupHeader: { display: 'flex', alignItems: 'center' },
});

const RoleDetail = ({ roleActions }: IRoleDetail): JSX.Element => {
  const { collapseGroups, toggleCollapseGroup } = useCollapsedGroups();
  const styles = useStyles();
  const sortedActions = roleActions
    ? [...roleActions?.actions].sort((a, b) => (a.category > b.category ? 1 : -1))
    : [];

  const getGroups = (): Array<IGroup> => {
    if (sortedActions) {
      const groupedActions = sortedActions.reduce(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (ga: any, item: { category?: string }) => {
          if (!ga[item.category]) {
            ga[item.category] = [];
          }

          ga[item.category].push(item);
          return ga;
        },
        {},
      );

      const groups: IGroup[] = Object.keys(groupedActions).map((category) => {
        const startIndex = sortedActions.findIndex(
          (ga: { category?: string }) => ga.category === category,
        );
        return {
          key: category,
          name: `${category}`,
          startIndex,
          count: groupedActions[category].length,
          level: 0,
          isCollapsed: false,
        };
      });
      return groups;
    }
    return [];
  };
  const groups = React.useMemo<IGroup[]>(() => getGroups(), [sortedActions]);

  return (
    <>
      {sortedActions && (
        <>
          <Card title={roleActions?.role?.name}>
            <Stack horizontal wrap horizontalAlign="space-around" tokens={{ childrenGap: 60 }}>
              <StackColumn>
                <SpanWithLabel labelText="Description" value={roleActions?.role?.description} />
              </StackColumn>
              <StackColumn>
                <SpanWithLabel labelText="Is Global" value={roleActions?.role?.isGlobal} />
              </StackColumn>
            </Stack>
          </Card>
          <br />
          <Card title="Actions">
            <FluentProvider theme={webLightTheme}>
              <Table aria-label="Actions table">
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={collapseGroups.All ? <ChevronRightRegular /> : <ChevronDownRegular />}
                        onClick={() =>
                          toggleCollapseGroup(
                            'All',
                            groups.map((g) => g.key),
                          )
                        }
                        aria-label={`${collapseGroups.All ? 'Expand' : 'Collapse'} All`}
                      />
                      <TableCellLayout> Display Name</TableCellLayout>
                    </TableHeaderCell>
                    <TableHeaderCell>Description</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groups.map((group) => {
                    const isCollapsed = !!collapseGroups[group.key];
                    return (
                      <React.Fragment key={group.key}>
                        <TableRow>
                          <TableCell>
                            <div className={styles.groupHeader}>
                              <Button
                                appearance="subtle"
                                size="small"
                                icon={
                                  isCollapsed ? <ChevronRightRegular /> : <ChevronDownRegular />
                                }
                                onClick={() => toggleCollapseGroup(group.key)}
                                aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${group.name}`}
                              />
                              <TableCellLayout>
                                {group.name} ({group.count})
                              </TableCellLayout>
                            </div>
                          </TableCell>
                          <TableCell />
                        </TableRow>
                        {!isCollapsed &&
                          sortedActions
                            .filter((item) => item.category === group.key)
                            .map((item) => {
                              return (
                                <TableRow key={item.id}>
                                  <TableCell>
                                    <TableCellLayout style={{ paddingLeft: '32px' }}>
                                      <Link to={`/role-description/actions/${item.id}`}>
                                        {item.displayName}
                                      </Link>
                                    </TableCellLayout>
                                  </TableCell>
                                  <TableCell>
                                    <TableCellLayout>{item.description}</TableCellLayout>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </FluentProvider>
          </Card>
        </>
      )}
    </>
  );
};

export default RoleDetail;
